// Keep wishlist stable across logout/login sessions unless user removes it.
export const WISHLIST_TTL_MS = Number.POSITIVE_INFINITY;

export const getFavoriteId = (item) => item?.id || item?._id || item?.propertyId || null;

const getCurrentUserIds = () => {
  try {
    const rawUser = localStorage.getItem('user');
    if (!rawUser) return [];

    const user = JSON.parse(rawUser);
    const ids = [user?._id, user?.id]
      .map((id) => (id != null ? String(id).trim() : ''))
      .filter(Boolean);

    return [...new Set(ids)];
  } catch {
    return [];
  }
};

const getPrimaryFavoritesKey = (requestedKey) => {
  const userIds = getCurrentUserIds();
  if (userIds.length > 0) {
    return `favorites_${userIds[0]}`;
  }
  return requestedKey || 'favorites';
};

const getPrimaryWishlistCountKey = (requestedKey) => {
  const userIds = getCurrentUserIds();
  if (userIds.length > 0) {
    return `wishlistCount_${userIds[0]}`;
  }
  return requestedKey || 'wishlistCount';
};

const getCandidateFavoriteKeys = (requestedKey) => {
  const primaryKey = getPrimaryFavoritesKey(requestedKey);
  const userIds = getCurrentUserIds();

  const keys = [primaryKey, requestedKey || 'favorites'];
  if (primaryKey !== 'favorites') {
    keys.push('favorites');
  }

  userIds.forEach((id) => keys.push(`favorites_${id}`));
  return [...new Set(keys.filter(Boolean))];
};

const normalizeWishlist = (favorites = []) => {
  const now = Date.now();
  const seenIds = new Set();
  const normalized = [];

  for (const entry of Array.isArray(favorites) ? favorites : []) {
    const id = getFavoriteId(entry);
    if (!id) continue;

    if (seenIds.has(String(id))) continue;
    seenIds.add(String(id));

    const addedAtRaw = Number(entry?.wishlistAddedAt);
    const wishlistAddedAt = Number.isFinite(addedAtRaw) && addedAtRaw > 0 ? addedAtRaw : now;

    if (now - wishlistAddedAt > WISHLIST_TTL_MS) continue;

    normalized.push({
      ...entry,
      wishlistAddedAt
    });
  }

  return normalized;
};

export const loadWishlistFavorites = (favoritesKey) => {
  try {
    const primaryKey = getPrimaryFavoritesKey(favoritesKey);
    const candidateKeys = getCandidateFavoriteKeys(favoritesKey);

    for (const key of candidateKeys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw);
      const normalized = normalizeWishlist(parsed);
      localStorage.setItem(key, JSON.stringify(normalized));

      if (normalized.length > 0) {
        if (key !== primaryKey) {
          localStorage.setItem(primaryKey, JSON.stringify(normalized));
        }
        return normalized;
      }
    }

    localStorage.setItem(primaryKey, JSON.stringify([]));
    return [];
  } catch (error) {
    console.error('Error loading wishlist favorites:', error);
    const primaryKey = getPrimaryFavoritesKey(favoritesKey);
    localStorage.setItem(primaryKey, JSON.stringify([]));
    return [];
  }
};

export const saveWishlistFavorites = (favoritesKey, wishlistCountKey, favorites) => {
  const normalized = normalizeWishlist(favorites);
  const primaryFavoritesKey = getPrimaryFavoritesKey(favoritesKey);
  const primaryWishlistCountKey = getPrimaryWishlistCountKey(wishlistCountKey);

  localStorage.setItem(primaryFavoritesKey, JSON.stringify(normalized));
  if (primaryWishlistCountKey) {
    localStorage.setItem(primaryWishlistCountKey, String(normalized.length));
  }
  return normalized;
};
